import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import {
  type DocumentStore,
  type ListDocumentsQueryContext,
} from '@/features/docs/DocumentStore'
import { type Document } from '@/features/docs/Document'
import {
  InfiniteScrollProvider,
  syncInfiniteScrollerItems,
} from '@/infra/InfiniteScrollProvider'
import type { GetCurrentUserFn, GetCurrentWorkspaceFn } from '@/features/iam'
import { type SnackbarRetryWrapperFactory } from '@/features/shell'
import { ViewModelList } from '@/infra/view-model-list'
import { action, computed, runInAction } from 'mobx'
import { SearchTextViewModel } from '@/infra/SearchTextViewModel'
import { task } from 'mobx-task'
import type { JobGistEvent } from '@/features/jobs/JobGist'
import { isNotNil } from '@/utils/object-util'
import { filter, merge, type Observable } from 'rxjs'
import { orderBy } from 'lodash-es'
import type { Id } from '@/infra/id'
import type { CursorPageQuery } from '@/infra/paging'
import type { DocumentUploaderViewModel } from '@/features/docs/DocumentUploaderViewModel'
import type { DocumentViewModel } from '@/features/docs/DocumentViewModel'
import type { InternalCreateDocumentViewModelFn } from '@/features/docs/internal'
import type { WellKnownDocumentTypeId } from '@/features/docs/DocumentType'
import { unpair } from '@/utils/rx-util'
/**
 * Options for {@link DocumentsListViewModel}.
 */
export interface DocumentsListViewModelOptions {
  /**
   * Where to query documents from. Can be for the user or a specific job.
   */
  context: DocumentsListViewModelQueryContext

  /**
   * Optional. The types of documents to fetch.
   * When specified, only documents of (any of) the given types will be fetched.
   */
  types?: WellKnownDocumentTypeId[]

  /**
   * Optional. The limit of documents to fetch per page.
   */
  limit?: number

  /**
   * Additional filter to apply for documents in the list.
   *
   * @param doc
   */
  filter?: (doc: Document) => boolean

  /**
   * If specified, will be called when the user interacts with a document item.
   * What that means is use-case/view-specific.
   *
   * @param doc
   */
  onAction?: (doc: Document) => Promise<unknown>

  /**
   * Called as part of removing or deleting a document.
   *
   * @param doc
   */
  onRemove?: (doc: Document) => Promise<unknown>

  /**
   * Whether to fetch the associated jobs on activate.
   *
   * @default false
   */
  fetchJobs?: boolean

  /**
   * Whether to fetch the author's user info on activate.
   *
   * @default false
   */
  fetchAuthor?: boolean
}

/**
 * Context for querying documents.
 */
export type DocumentsListViewModelQueryContext =
  | { type: 'User' }
  | { type: 'Job'; jobId: Id }

/**
 * View model for listing documents.
 * The caller should call {@link fetch} when they want to trigger a fetch
 * for documents, as it won't be done on activate.
 * When the VM is active, real-time changes and newly uploaded docs will be
 * synced to the underlying scroller.
 */
export class DocumentsListViewModel implements ActivationLifecycle {
  private readonly _documentVms
  private readonly _lifecycle = new LifecycleTracker()

  /**
   * Search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Scroller for listing the documents.
   */
  readonly scroller

  constructor(
    private readonly _options: DocumentsListViewModelOptions,
    getCurrentUser: GetCurrentUserFn,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
    jobGistRealtime$: Observable<JobGistEvent>,
    private readonly _createSnackbarRetryWrapper: SnackbarRetryWrapperFactory,
    private readonly _documentStore: DocumentStore,
    createDocumentViewModel: InternalCreateDocumentViewModelFn,
    public readonly uploaderVm: DocumentUploaderViewModel,
  ) {
    this.scroller = new InfiniteScrollProvider({
      limit: this._options.limit ?? 50,
      fetchItems: ({ paging, signal }) => this._fetchItems(paging, signal),
    })
    this._filter = this._filter.bind(this)

    this._documentVms = new ViewModelList({
      models: () =>
        // Filter and order the models.
        orderBy(
          this.scroller.items.filter(this._filter),
          (d) => d.dateCreated,
          'desc',
        ),
      create: (m) =>
        createDocumentViewModel(m, {
          fetchAuthor: Boolean(this._options.fetchAuthor),
          fetchJobs: Boolean(this._options.fetchJobs),
          onAction: () => this._options.onAction?.(m) ?? Promise.resolve(),
          onRemove: () => this._options.onRemove?.(m) ?? Promise.resolve(),
        }),
      activate: (v) => v.activate(),
      deactivate: (v) => v.deactivate(),
    })

    this._lifecycle.manage(this.uploaderVm)
    this._lifecycle.manage(this._documentVms)
    this._lifecycle.manage(jobGistRealtime$)
    this._lifecycle.manage(
      syncInfiniteScrollerItems({
        scroller: this.scroller,
        removals$: merge(
          this._documentStore.documentRemoved$,
          // Also remove documents where the access has been removed
          // for the current `context`
          this._documentStore.documentAccessRemoved$.pipe(
            filter(([, access]) => {
              switch (this._options.context.type) {
                case 'Job': {
                  return (
                    access.accessType.type === 'Job' &&
                    access.accessType.jobId === this._options.context.jobId
                  )
                }
              }
              return false
            }),
            unpair(),
          ),
        ),
        additions$: merge(
          this._documentStore.documentAdded$,
          this.uploaderVm.uploaded$,
          // The VM already filters based on the context, so no need
          // to look at the access here.
          this._documentStore.documentAccessAdded$.pipe(unpair()),
        ).pipe(
          // Filter on docs belonging to the workspace we're displaying docs for,
          // and belonging to the current user.
          filter(
            (d) =>
              d.workspaceId === this._getCurrentWorkspace().id &&
              d.ownerUserId === getCurrentUser().id,
          ),
        ),
      }),
    )
  }

  /**
   * All currently loaded documents, with the consumer filter applied.
   */
  @computed
  get allDocuments() {
    return this._documentVms.items
  }

  /**
   * Filtered documents based on search text, if any.
   */
  @computed
  get filteredDocuments(): readonly DocumentViewModel[] {
    const searchText = this.searchText.value.toLowerCase()
    if (!searchText) {
      return this.allDocuments
    }

    return this.allDocuments.filter(
      (dvm) =>
        dvm.doc.title.toLowerCase().includes(searchText) ||
        dvm.doc.typeName?.toLowerCase().includes(searchText) ||
        dvm.jobs.some((j) => j.title.toLowerCase().includes(searchText)),
    )
  }

  /**
   * Which empty state to display, if any.
   */
  @computed
  get emptyState(): 'NoDocuments' | 'NoSearchResults' | false {
    if (this.scroller.isDone) {
      if (this.allDocuments.length === 0) {
        return 'NoDocuments'
      }

      if (this.filteredDocuments.length === 0) {
        return 'NoSearchResults'
      }
    }

    return false
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * Triggers a fetch for the documents.
   */
  fetch = task(async (signal?: AbortSignal) => {
    await this.scroller.fetch({
      staleWhileRevalidate: true,
      signal: AbortSignal.any(
        [this._lifecycle.signal, signal].filter(isNotNil),
      ),
    })
  })

  /**
   * Fetches specific documents by their long ID and adds them to the list.
   */
  fetchByIds = task(async (longIds: readonly Id[], signal?: AbortSignal) => {
    const fetched = await Promise.all(
      longIds.map((longId) => this._documentStore.fetchDocument(longId)),
    ).then((list) => list.filter(isNotNil))

    if (signal?.aborted) {
      return
    }

    runInAction(() => {
      fetched.forEach(this.addDocument)
    })
  })

  /**
   * Adds a document to the scroller.
   */
  @action.bound
  addDocument(doc: Document) {
    this.scroller.add(doc)
  }

  /**
   * Removes a document from the scroller.
   */
  @action.bound
  removeDocument(doc: Document) {
    this.scroller.removeWhere((d) => d.id === doc.id)
  }

  /**
   * Fetches the items for the scroller.
   */
  private async _fetchItems(paging: CursorPageQuery, signal: AbortSignal) {
    const withRetry = this._createSnackbarRetryWrapper(
      'Unable to load documents',
      signal,
    )

    const context: ListDocumentsQueryContext =
      this._options.context.type === 'User'
        ? { type: 'User', workspaceId: this._getCurrentWorkspace().id }
        : { type: 'Job', jobId: this._options.context.jobId }

    return await withRetry(() => {
      return this._documentStore.listDocuments(
        {
          ...paging,
          context,
          types: this._options.types,
        },
        signal,
      )
    })
  }

  /**
   * Applies the caller-specified filter, as well as filters out
   * documents being uploaded.
   */
  private _filter(doc: Document) {
    // Filter out documents currently being uploaded.
    const isUploading = this.uploaderVm.isUploading(doc)
    if (isUploading) {
      return false
    }

    // If the document doesn't have a file, don't show it in the list.
    if (!doc.isFullyUploaded) {
      return false
    }

    // Filter based on query context.
    if (this._options.context.type === 'Job') {
      if (!doc.isLinkedToJob(this._options.context.jobId)) {
        return false
      }
    }

    // Filter based on types.
    if (this._options.types?.length) {
      // Note on cast: this is safe because it's just an inclusion check.
      const typeId = doc.typeId as WellKnownDocumentTypeId
      if (!this._options.types.includes(typeId)) {
        return false
      }
    }

    // Exclude documents that require manual Pro verification.
    if (
      doc.file?.state === 'ManualVerificationRequired' ||
      doc.file?.state === 'Pending' ||
      doc.file?.state === 'VerifyingClientData'
    ) {
      return false
    }

    // Filter based on the caller-specified filter.
    if (this._options.filter) {
      return this._options.filter(doc)
    }

    return true
  }
}
